import React, { Component } from 'react';
import { set, find } from 'lodash/fp';
import PropTypes from 'prop-types';
import {
  Form,
  FormControl,
  SelectPicker,
  Modal,
  Button,
  ButtonToolbar,
  Schema
} from 'rsuite';
import history from '../../../routes/history';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import { modifyAccess } from '../../../helpers/permission';
import AccessControl from '../../AccessControl';
import { inventoryCreateItemPath } from '../../../routes/Paths';
import { FormGroup, ControlLabel } from '../../widgets/FormElements';
import {
  ItemType,
  PackageType
} from '../../../constants/inventory/InventoryTypes';
import { parseInt } from '../../../helpers/utils';

const FormModel = Schema.Model({
  sku: Schema.Types.StringType().isRequired('This field is required.'),
  itemId: Schema.Types.NumberType().isRequired('This field is required.')
});

const DefaultFormValue = {
  sku: '',
  itemId: null
};

const ItemOptionType = PropTypes.arrayOf(
  PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })
);

export default class InventoryAddSkuModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    itemId: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    sku: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
    itemOptions: ItemOptionType.isRequired,
    packageOptions: ItemOptionType.isRequired,
    isSavingItem: PropTypes.bool.isRequired,
    isSavedItem: PropTypes.bool.isRequired,
    isSavingPackage: PropTypes.bool.isRequired,
    isSavedPackage: PropTypes.bool.isRequired,
    isFetchingItemOptions: PropTypes.bool.isRequired,
    isItemOptionsFetched: PropTypes.bool.isRequired,
    isFetchingPackageOptions: PropTypes.bool.isRequired,
    onInit: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired
  };

  static defaultProps = {
    itemId: null
  };

  constructor(props) {
    super(props);
    this.state = {
      formValue: DefaultFormValue,
      formError: {}
    };
    this.formRef = React.createRef();
  }

  componentDidMount() {
    const { isItemOptionsFetched, onInit } = this.props;
    if (!isItemOptionsFetched) onInit();
  }

  componentDidUpdate({
    isSavedItem: prevIsSavedItem,
    isSavedPackage: prevIsSavedPackage
  }) {
    const { isSavedItem, isSavedPackage, onHide } = this.props;
    if (
      (!prevIsSavedItem && isSavedItem) ||
      (!prevIsSavedPackage && isSavedPackage)
    ) {
      onHide();
    }
  }

  get getItemType() {
    const { formValue } = this.state;
    const { packageOptions } = this.props;
    return find({ value: parseInt(formValue.itemId) }, packageOptions)
      ? PackageType
      : ItemType;
  }

  static getDerivedStateFromProps({ itemId, sku }, state) {
    const { formValue } = state;
    return !formValue.sku && sku
      ? set('formValue', { itemId, sku }, state)
      : state;
  }

  handleModalExited = () => {
    this.setState({ formValue: DefaultFormValue });
  };

  handleFormChange = data => {
    const { formValue } = this.state;
    this.setState({ formValue: { ...formValue, ...data } });
  };

  handleSubmit = () => {
    if (!this.formRef.current.check()) {
      return;
    }

    const { formValue } = this.state;
    const { onSubmit } = this.props;
    onSubmit({ ...formValue, type: this.getItemType });
  };

  handleCreateItem = () => {
    const { onHide } = this.props;
    const { formValue } = this.state;
    onHide();
    setTimeout(() => {
      history.push(`${inventoryCreateItemPath}?sku=${formValue.sku}`);
    }, 100);
  };

  render() {
    const { formValue, formError } = this.state;
    const {
      show,
      onHide,
      isSavingItem,
      isSavingPackage,
      isFetchingItemOptions,
      isFetchingPackageOptions,
      itemOptions,
      packageOptions
    } = this.props;

    const isSaving = isSavingItem || isSavingPackage;
    const isFetchingOptions = isFetchingItemOptions || isFetchingPackageOptions;
    const allOptions = !isFetchingOptions
      ? [
          ...itemOptions.map(set('type', 'Item')),
          ...packageOptions.map(set('type', 'Package'))
        ]
      : [];

    return (
      <Modal
        size="xs"
        show={show}
        onShow={this.handleOnShow}
        onHide={onHide}
        onExited={this.handleModalExited}
      >
        <Modal.Header>
          <Modal.Title>Add Item/Package SKU</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ marginTop: 10 }}>
          <Form
            fluid
            model={FormModel}
            ref={this.formRef}
            formValue={formValue}
            onChange={this.handleFormChange}
            onCheck={error => {
              this.setState({ formError: error });
            }}
            style={{ paddingBottom: 10 }}
          >
            <FormGroup>
              <ControlLabel>Barcode</ControlLabel>
              <FormControl name="sku" errorMessage={formError.sku} />
            </FormGroup>
            <FormGroup>
              <ControlLabel>Item/Package</ControlLabel>
              <FormControl
                style={{ width: '100%' }}
                name="itemId"
                placeholder={
                  isFetchingOptions ? 'loading...' : 'Select Item/Package'
                }
                groupBy="type"
                data={allOptions}
                accepter={SelectPicker}
                renderMenuGroup={label => <strong>{label}</strong>}
                errorMessage={formError.itemId}
              />
            </FormGroup>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <ButtonToolbar>
            <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.ITEM)}>
              <Button
                color="green"
                onClick={this.handleCreateItem}
                style={{ float: 'left' }}
                disabled={!formValue.sku || !!formValue.itemId}
              >
                Create New Item
              </Button>
            </AccessControl>
            <Button
              onClick={this.handleSubmit}
              appearance="primary"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={onHide}>Cancel</Button>
          </ButtonToolbar>
        </Modal.Footer>
      </Modal>
    );
  }
}
